import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';

import EducationForm from '@app/components/Pages/Education/EducationForm';
import Pagination from '@app/components/Pages/Education/Pagination';
import Post from '@app/components/Pages/Education/Post';
import LegendList from '@app/components/Pages/Education/TopicList';
import Background from '@app/components/UI/Background/Background';
import { HelmetTags } from '@app/components/UI/Helmet/HelmetCustom';
import Tabs from '@app/components/UI/Tabs/Tabs';
import { BlogPost, BlogTopic, EducationPage } from '@app/objects/Education';
import '@app/scss/pages/education.scss';

interface BlockPostsProps {
	pagePosts: EducationPage;
}

const BlockPosts: React.FC<BlockPostsProps> = ({ pagePosts }) => {
	const history = useHistory();
	const location = useLocation();
	const params = useParams<{ category: string }>();

	const [tabs, setTabs] = React.useState([
		{
			key: 'all',
			label: 'All',
			children: <></>,
			id: 0,
		},
	]);
	const [tabActive, setTabActive] = React.useState(tabs[0]);

	const [pageActive, setPageActive] = React.useState<number>(1);

	const [topics, setTopics] = React.useState<any>();
	const [topicActive, setTopicActive] = React.useState<null | BlogTopic>(null);

	const [valueInput, setValueInput] = React.useState<string>('');

	const [searchParams, setSearchParams] = React.useState(new URLSearchParams(location.search));

	const context = useItemsProviderContext<BlogPost>();
	if (!context.actions) throw 'Need ItemsProvider context!';
	const {
		state: { items, pagination },
		actions: { handleChange },
	} = context;

	const changePage = (page) => {
		setPageActive(page);
	};

	const changeTab = (item) => {
		setTabActive(item);
		setPageActive(1);
	};

	const changeInput = (str) => {
		setValueInput(str.currentTarget.value);
	};

	const selectTopicPost = (topicPost: BlogTopic) => {
		const topic = topics.filter((topic) => topic.item.name === topicPost.name);
		setTopicActive(topic.length ? topic[0].item : null);

		setPageActive(1);
		window.scrollTo(0, 300);
	};

	React.useEffect(() => {
		if (pagePosts) {
			setTabs([
				...tabs,
				...pagePosts.categories.map((category) => {
					return {
						key: category.urlPath,
						label: category.name,
						children: <></>,
						id: category.id,
					};
				}),
			]);
			setTopics(
				pagePosts.topics.map((topic) => {
					return {
						key: topic.name,
						value: topic.name,
						label: topic.name,
						item: topic,
					};
				}),
			);
		}
	}, []);

	React.useEffect(() => {
		if (tabs.length > 1) {
			if (params.category) {
				const tab = tabs.filter((tab) => tab.key === params.category);
				setTabActive(tab.length ? tab[0] : tabs[0]);
			}

			if (!searchParams.size) {
				setSearchParams(
					new URLSearchParams({
						topic: topicActive?.name ? topicActive.name : 'all',
						page: String(pageActive),
					}),
				);
			} else {
				const topic = topics.filter((topic) => topic.item.name === searchParams.get('topic'));
				setTopicActive(topic.length ? topic[0].item : null);

				const page = searchParams.get('page');
				setPageActive(page ? Number(page) : 1);
			}
		}
	}, [tabs]);

	React.useEffect(() => {
		if (tabs.length > 1 && searchParams.size) {
			setSearchParams(
				new URLSearchParams({
					topic: topicActive?.name ? topicActive.name : 'all',
					page: String(pageActive),
				}),
			);
		}
	}, [pageActive, topicActive]);

	React.useEffect(() => {
		if (tabs.length > 1 && searchParams.size) {
			if (tabActive.id) {
				history.push(`/education/${tabActive.key}?${searchParams}`);
			} else {
				history.push(`/education?${searchParams}`);
			}
		}
	}, [tabActive, searchParams]);

	React.useEffect(() => {
		if (tabs.length > 1 && pageActive > 1) {
			setPageActive(1);
		}
	}, [valueInput]);

	React.useEffect(() => {
		if (tabs.length > 1) {
			handleChange({
				text: valueInput,
				categoryId: tabActive.id ? tabActive.id : null,
				topicId: topicActive ? topicActive.id : null,
				page: pageActive,
			});
		}
	}, [tabActive, topicActive, pageActive, valueInput]);

	return (
		pagePosts && (
			<>
				<div className="container">
					<Tabs items={tabs} onItemChange={changeTab} defaultKey={tabActive.key} />
					<div className="filters">
						<SimpleSearchInput
							value={valueInput}
							onChange={changeInput}
							buttonContent={
								<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
									<path
										d="M30 29L25.65 24.65M28 19C28 23.4183 24.4183 27 20 27C15.5817 27 12 23.4183 12 19C12 14.5817 15.5817 11 20 11C24.4183 11 28 14.5817 28 19Z"
										stroke="#5D4493"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							}
						/>
						<LegendList
							multiple={false}
							title="Topics:"
							value={topicActive}
							items={topics ?? [{
								key: '',
								value: '',
								label: <></>,
								item: [],
							}]}
							onSelectItem={(value) => {
								setTopicActive(value);
								setPageActive(1);
							}}
						/>
					</div>
					{items.length ? (
						<div className="posts">
							{items.map((item, i) => (
								<Post key={item.id} item={item} lazy={i > 4} onTopic={selectTopicPost} topicActive={topicActive?.name} />
							))}
						</div>
					) : (
						<div className="h2 text-center">Nothing found</div>
					)}
				</div>
				{items.length ? <Pagination current={pageActive} total={pagination.total || pagePosts.items.count} onChange={changePage} /> : null}
			</>
		)
	);
};

const Education: React.FC = () => {
	return (
		<div className="education-page is-relative pages">
			<Background />
			<div className="container text-center">
				<h1 className="h2">Education</h1>
				<p className="h4">
					Welcome to the dedicated section for senior pet care, your go-to resource for expert advice and insights on providing the best
					possible care for your aging furry friends. Dive into a wealth of informative articles tailored to address the unique issues and
					needs of senior dogs, cats, and other beloved companions, ensuring they live a happy, healthy, and fulfilling life. Explore our
					articles below to start your journey towards compassionate senior pet care.
				</p>
			</div>
			<div className="bg-white-opacity" style={{ paddingTop: 0 }}>
				<div className="education-page__posts">
					<ServerPageProvider
						path="education"
						inner={(pagePosts: EducationPage) => {
							return (
								<ItemsProvider<BlogPost>
									type="blogPost"
									loadRequest="blogPostList"
									items={pagePosts.items?.list}
									pagination={{
										pageSize: 8,
										pageSizeOptions: [8],
									}}
								>
									<HelmetTags lang={pagePosts.langs[0]} title="Education" />
									<BlockPosts pagePosts={pagePosts} />
								</ItemsProvider>
							);
						}}
					/>
				</div>
			</div>
			<EducationForm />
		</div>
	);
};

export default Education;
